// ════════════════════════════════════════════════════════════════════
// CLIENT MATCHER — duplicate-prevention for carrier imports.
//
// Five rules in order, mirroring the user's spec:
//
//   1. Carrier Policy Number + Carrier Name
//   2. Email Address
//   3. Mobile Phone Number
//   4. First Name + Last Name + Date of Birth
//   5. First Name + Last Name + Address
//
// On match: return { match, matchedOn }. On miss: { match: null }.
//
// Multi-tenant safety: every query filters by ownerId. There is no
// cross-account read path here; even if RLS were misconfigured,
// the ownerId clause keeps us inside the agency's roster.
//
// PHONE / NAME normalization
// ──────────────────────────────────────────────────────────────────
// · Phone: strip everything except digits; if 11-digit starts
//   with '1', drop the leading 1; final form is 10 raw digits.
//   We MATCH on raw 10-digit so "+1 (713) 555-1212" finds
//   "7135551212".
// · Email: lowercased on both sides.
// · Name: lowercased + collapsed whitespace. fuzzyNameMatch()
//   uses token-overlap so "John Q Smith" matches "John Smith".
// · Carrier: lowercased + stripped of common suffixes
//   ("inc", "corp", "insurance co", "ins", etc.) so
//   "Mutual of Omaha" matches "Mutual of Omaha Insurance Co."
// ════════════════════════════════════════════════════════════════════

import { supabase } from './supabase';

export function normalizePhone(s) {
  const d = String(s || '').replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) return d.slice(1);
  return d.length >= 10 ? d.slice(-10) : d;
}

export function normalizeName(s) {
  return String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

export function normalizeCarrier(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/[.,]/g, '')
    .replace(/\b(insurance|insurer|ins|inc|llc|corp|company|co|of america|america|usa|the|group)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function fuzzyNameMatch(a, b) {
  const ta = new Set(normalizeName(a).split(' ').filter(Boolean));
  const tb = new Set(normalizeName(b).split(' ').filter(Boolean));
  if (ta.size === 0 || tb.size === 0) return false;
  let overlap = 0;
  for (const t of ta) if (tb.has(t)) overlap++;
  // Need at least 2 token overlap if either side has 2+ tokens,
  // else require the single token match. This handles "Mya Taylor"
  // vs "Mya M Taylor" (overlap=2) without firing on "Mya Brown"
  // vs "Mya Taylor" (overlap=1).
  const minNeeded = Math.min(ta.size, tb.size) >= 2 ? 2 : 1;
  return overlap >= minNeeded;
}

// Pull out first/last from a full-name string. "Smith, John" and
// "John Smith" both resolve correctly.
export function splitName(full) {
  const s = String(full || '').trim();
  if (!s) return { first: '', last: '' };
  if (s.includes(',')) {
    const [last, first] = s.split(',').map(t => t.trim());
    return { first: first || '', last: last || '' };
  }
  const parts = s.split(/\s+/);
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts[0], last: parts[parts.length - 1] };
}

// Build the normalized candidate the matcher consumes. Parsers
// (HCSC, Jarvis, HealthSherpa, generic CSV) call this with their
// row shape; the candidate is the lingua franca the rest of the
// pipeline speaks.
export function buildCandidate({
  fullName, firstName, lastName, email, phone, dob, address,
  policyNumber, carrier, planType, format,
}) {
  let first = firstName, last = lastName;
  if (!first && !last && fullName) {
    const split = splitName(fullName);
    first = split.first; last = split.last;
  }
  const name = (fullName || `${first || ''} ${last || ''}`.trim()).trim();
  return {
    name,
    firstName:    first || '',
    lastName:     last || '',
    email:        String(email || '').toLowerCase().trim() || null,
    phone:        normalizePhone(phone) || null,
    dob:          dob || null,             // YYYY-MM-DD
    address:      String(address || '').trim() || null,
    policyNumber: String(policyNumber || '').trim() || null,
    carrier:      String(carrier || '').trim() || null,
    planType:     planType || null,
    format:       format || null,
  };
}

// ── The matcher — runs all 5 rules in order, returns first hit ─
// Returns:
//   { match: <clients row> | null, matchedOn: enum | null }
//
// `existing` is an in-memory list of all the operator's clients —
// passed in so a batch of 200 rows only hits the DB once instead
// of 200 times. Pass an empty array if you want each call to
// query directly (slower, fine for one-off uses).
//
// `policyIndex` (optional) lets the rule-1 lookup short-circuit:
// a Map<`${ownerId}::${memberId.toLowerCase()}::${carrierNorm}`, clientId>.
export function matchClientInMemory({ ownerId, candidate, clients, policyIndex }) {
  // Rule 1: Carrier Policy Number + Carrier Name
  if (candidate.policyNumber && candidate.carrier && policyIndex) {
    const key = `${ownerId}::${candidate.policyNumber.toLowerCase()}::${normalizeCarrier(candidate.carrier)}`;
    const clientId = policyIndex.get(key);
    if (clientId) {
      const match = clients.find(c => c.id === clientId);
      if (match) return { match, matchedOn: 'policy_number_carrier' };
    }
  }

  // Rule 2: Email
  if (candidate.email) {
    const m = clients.find(c => c.email && c.email.toLowerCase() === candidate.email);
    if (m) return { match: m, matchedOn: 'email' };
  }

  // Rule 3: Phone
  if (candidate.phone) {
    const m = clients.find(c => normalizePhone(c.phone) === candidate.phone);
    if (m) return { match: m, matchedOn: 'phone' };
  }

  // Rule 4: First + Last + DOB
  if (candidate.firstName && candidate.lastName && candidate.dob) {
    const m = clients.find(c =>
      c.dob === candidate.dob &&
      fuzzyNameMatch(c.name, candidate.name)
    );
    if (m) return { match: m, matchedOn: 'name_dob' };
  }

  // Rule 5: First + Last + Address
  if (candidate.firstName && candidate.lastName && candidate.address) {
    const normAddr = candidate.address.toLowerCase().replace(/\s+/g, ' ').trim();
    const m = clients.find(c => {
      if (!c.address) return false;
      const ca = c.address.toLowerCase().replace(/\s+/g, ' ').trim();
      if (ca !== normAddr) return false;
      return fuzzyNameMatch(c.name, candidate.name);
    });
    if (m) return { match: m, matchedOn: 'name_address' };
  }

  return { match: null, matchedOn: null };
}

// Load all of the owner's clients + a (member_id, carrier) → client_id
// index for rule-1 short-circuit. Single DB roundtrip per import.
export async function loadOwnerMatcherContext(ownerId) {
  const [{ data: clients }, { data: policies }] = await Promise.all([
    supabase.from('clients')
      .select('id, name, phone, email, dob, address, policy, carrier, vertical, sector')
      .eq('owner_id', ownerId),
    supabase.from('health_policies')
      .select('id, client_id, carrier, member_id')
      .eq('owner_id', ownerId)
      .not('client_id', 'is', null),
  ]);

  const policyIndex = new Map();

  // Health-policies index — (owner, member_id, carrier-normalized).
  (policies || []).forEach(p => {
    if (!p.member_id || !p.carrier || !p.client_id) return;
    const k = `${ownerId}::${p.member_id.toLowerCase()}::${normalizeCarrier(p.carrier)}`;
    if (!policyIndex.has(k)) policyIndex.set(k, p.client_id);
  });

  // Legacy life-side single-policy storage on the clients row itself.
  (clients || []).forEach(c => {
    if (!c.policy || !c.carrier) return;
    const k = `${ownerId}::${c.policy.toLowerCase()}::${normalizeCarrier(c.carrier)}`;
    if (!policyIndex.has(k)) policyIndex.set(k, c.id);
  });

  return { clients: clients || [], policyIndex };
}

// Compute the diff between an existing client row and a fresh
// candidate. Used for the "changes" column on import_run_items
// + the audit log metadata. Only fills in fields where the
// candidate has a non-null/non-empty value that differs from
// what's on record. NEVER overwrites an existing value with a
// blank — fresh data wins only when it IS fresh.
export function diffClient(existing, candidate) {
  const diff = {};
  const consider = ['name', 'phone', 'email', 'dob', 'address'];
  consider.forEach(k => {
    const newV = candidate[k];
    if (newV == null || newV === '') return;
    const oldV = existing[k];
    if (oldV === newV) return;
    if (k === 'phone' && normalizePhone(oldV) === normalizePhone(newV)) return;
    if (k === 'email' && String(oldV || '').toLowerCase() === String(newV).toLowerCase()) return;
    diff[k] = [oldV ?? null, newV];
  });
  return diff;
}
